import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { TextLinkBack } from '@nebula/text-link';
import SegmentedControl from './SegmentedControl';
import PlanPanel from './PlanPanel';
import ActionContainer from './ActionContainer';
import useCarouselGestures from './hooks/useCarouselGestures';
import { SEGMENTED_COLORS, STICKY_HEADER_SCROLL_THRESHOLD_PX } from './constants';
import {
  PageContainer,
  CarouselViewport,
  ScrollContent,
  GradientBand,
  CarouselTrack,
  CardGap,
  PlanPanelWrapper,
  Fade,
  PageTitle,
  TitleArea,
  StickyHeader,
  StickyHeaderTitle,
  SegmentedControlArea,
  MobileErrorAlertArea,
  MobileErrorAlertContainer,
} from './index.styles';
import { ErrorAlert } from '@nebula/alert';
import type { PlanId, SubscriptionData, Feature, OrderedPlan, ErrorState } from '../types';
import products, { flexProducts, normalizeProductFamily } from '../../../constants/products';

const MOBILE_PLAN_ORDER: string[] = [products.basic, products.identity, products.credit_expert];

interface MySubscriptionsMobileProps {
  currentPlan: string;
  subscriptionData: SubscriptionData;
  isWebview?: boolean;
  switchPermitted: boolean;
  errorState: ErrorState;
  onBack: (() => void) | null;
  orderedPlans: OrderedPlan[];
  toggleManageDrawer?: () => void;
  onSelectPlan: (plan: OrderedPlan) => Promise<void>;
  onSelectFeature: (feature: Feature) => void;
  onVoucher?: () => void;
}

const MySubscriptionMobile: React.FC<MySubscriptionsMobileProps> = ({
  currentPlan: currentFamily,
  subscriptionData,
  isWebview = false,
  switchPermitted,
  errorState,
  onBack,
  orderedPlans,
  toggleManageDrawer,
  onSelectPlan,
  onSelectFeature,
  onVoucher,
}) => {
  const currentPlan = currentFamily as PlanId;

  const visibleErrors = useMemo(() => Object.values(errorState).filter(error => Boolean(error?.show)), [errorState]);

  const orderedMobilePlans = useMemo(
    () =>
      MOBILE_PLAN_ORDER.map(planId => orderedPlans.find(plan => plan.family === planId)).filter(
        Boolean
      ) as OrderedPlan[],
    [orderedPlans]
  );
  const tabs = useMemo(
    () => orderedMobilePlans.map(({ id, tabLabel }) => ({ id, label: tabLabel })),
    [orderedMobilePlans]
  );

  const defaultTabIndex = tabs.findIndex(t => t.id === currentPlan);
  const initialTabIndex = defaultTabIndex === -1 ? 0 : defaultTabIndex;
  const [activeTab, setActiveTab] = useState(initialTabIndex);

  const [isSticky, setIsSticky] = useState(false);

  const { trackRef, viewportRef, goToTab } = useCarouselGestures({
    tabs,
    initialTabIndex,
    onTabChange: setActiveTab,
  });

  // Driven by scroll position rather than an IntersectionObserver, because the sticky
  // state itself resizes `viewport` (it collapses the title/error/tabs above it to give
  // the carousel more room) — observing that same viewport's geometry would re-trigger
  // on its own resize and oscillate. scrollTop is unaffected by that resize.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const handleScroll = () => {
      setIsSticky(viewport.scrollTop > STICKY_HEADER_SCROLL_THRESHOLD_PX);
    };
    handleScroll();
    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [viewportRef]);

  const activeTabId = tabs[activeTab].id;
  const { track: trackBg, activeItem: activeItemBg } = SEGMENTED_COLORS[activeTabId];
  const activePlan = orderedMobilePlans[activeTab];

  const handleSelectPlan = useCallback(async () => {
    const plan = orderedPlans.find(p => p.id === activeTabId);
    if (plan) {
      await onSelectPlan(plan);
    }
  }, [activeTabId, orderedPlans, onSelectPlan]);

  const activeOffer = useMemo(
    () => orderedMobilePlans.find(p => p.id === activeTabId)?.product,
    [orderedMobilePlans, activeTabId]
  );

  return (
    <PageContainer $plan={activeTabId} $webview={isWebview}>
      {!isWebview && (
        <TitleArea $visible={!isSticky}>
          {onBack && <TextLinkBack onClick={onBack}>Back</TextLinkBack>}
          <PageTitle>My subscription</PageTitle>
        </TitleArea>
      )}
      {visibleErrors.length > 0 && (
        <MobileErrorAlertArea $visible={!isSticky} $webview={isWebview}>
          {visibleErrors.map(({ id, heading, message }) => (
            <MobileErrorAlertContainer key={id} id={id}>
              <ErrorAlert title={heading} message={message ?? ''} showIcon />
            </MobileErrorAlertContainer>
          ))}
        </MobileErrorAlertArea>
      )}

      <StickyHeader $plan={activeTabId} $visible={isSticky} $webview={isWebview}>
        <StickyHeaderTitle $plan={activeTabId}>
          {activePlan?.cardTitle ?? subscriptionData.plans[activeTab]?.cardTitle}
        </StickyHeaderTitle>
      </StickyHeader>
      <SegmentedControlArea $visible={!isSticky} $plan={activeTabId}>
        <SegmentedControl
          tabs={tabs}
          activeTabIndex={activeTab}
          onTabChange={goToTab}
          trackBg={trackBg}
          activeItemBg={activeItemBg}
          activeItemTextColor={trackBg}
          ariaLabel="Plans"
        />
      </SegmentedControlArea>

      <CarouselViewport ref={viewportRef}>
        <ScrollContent>
          <GradientBand $plan={activeTabId} $tabIndex={activeTab} />
          <CarouselTrack ref={trackRef}>
            {orderedMobilePlans.map((plan, index) => {
              const isActivePanel = index === activeTab;

              return (
                <React.Fragment key={plan.family}>
                  {index > 0 && <CardGap />}
                  {/* Peeking cards stay visible, but only the selected plan is exposed to
                      assistive tech. Transform order would otherwise be read left to right,
                      so a screen reader starts on the leftmost sliver instead of the current plan. */}
                  <PlanPanelWrapper
                    aria-hidden={isActivePanel ? undefined : true}
                    ref={(node: HTMLDivElement | null) => {
                      if (!node) return;
                      if (isActivePanel) node.removeAttribute('inert');
                      else node.setAttribute('inert', '');
                    }}
                  >
                    <PlanPanel
                      plan={plan}
                      featureSections={subscriptionData.featureSections}
                      currentPlan={currentPlan}
                      price={plan.family === normalizeProductFamily(flexProducts.free) ? null : plan.priceSummary}
                      onSelectFeature={onSelectFeature}
                    />
                  </PlanPanelWrapper>
                </React.Fragment>
              );
            })}
          </CarouselTrack>
        </ScrollContent>
      </CarouselViewport>

      <Fade />
      <ActionContainer
        currentPlan={currentPlan}
        activeTabId={activeTabId}
        isWebview={isWebview}
        switchPermitted={switchPermitted}
        product={activeOffer}
        planName={activePlan?.cardTitle}
        onSelectPlan={handleSelectPlan}
        onManage={toggleManageDrawer}
        onVoucher={onVoucher}
      />
    </PageContainer>
  );
};

export default MySubscriptionMobile;
